import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  ShieldCheck, Cpu, ArrowRight, ChevronDown, ChevronUp,
  Download, RefreshCw, FileText, Activity, UserCog, Users, Sparkles,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { exportClassificationAuditJSON } from '@/lib/api/classificationAuditExport';

interface AuditEvent {
  id: string;
  created_at: string;
  activity_type: string;
  entity_type: string;
  metadata: any;
  user_name: string;
  user_email: string;
}

interface AuditTrailPanelProps {
  workspaceId: string;
  embedded?: boolean;
}

const EVENT_CONFIG: Record<string, {
  label: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}> = {
  classification_overridden: {
    label: 'Override',
    icon: Cpu,
    color: 'text-amber-600',
    bgColor: 'bg-amber-500/10 border-amber-500/20',
  },
  classification_confirmed: {
    label: 'Bestätigt',
    icon: ShieldCheck,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-500/10 border-emerald-500/20',
  },
  decision_created: {
    label: 'Erstellt',
    icon: FileText,
    color: 'text-primary',
    bgColor: 'bg-primary/10 border-primary/20',
  },
  phase_changed: {
    label: 'Phase',
    icon: Activity,
    color: 'text-blue-600',
    bgColor: 'bg-blue-500/10 border-blue-500/20',
  },
  role_changed: {
    label: 'Rolle',
    icon: UserCog,
    color: 'text-purple-600',
    bgColor: 'bg-purple-500/10 border-purple-500/20',
  },
  member_joined: {
    label: 'Beigetreten',
    icon: Users,
    color: 'text-teal-600',
    bgColor: 'bg-teal-500/10 border-teal-500/20',
  },
};

function getEventDescription(event: AuditEvent): string {
  const m = event.metadata || {};
  switch (event.activity_type) {
    case 'classification_overridden': {
      const delta = m.override_delta || {};
      const parts: string[] = [];
      if (delta.from_type && delta.to_type) parts.push(`${delta.from_type} → ${delta.to_type}`);
      else if (m.system_classified_type && m.final_type) parts.push(`${m.system_classified_type} → ${m.final_type}`);
      if (m.system_confidence) parts.push(`(${m.system_confidence}%)`);
      return parts.length > 0 ? parts.join(' ') : 'Klassifizierung manuell korrigiert';
    }
    case 'classification_confirmed':
      return `${m.system_classified_type || '?'}/${m.system_classified_subtype || '?'} · ${m.system_confidence || 0}%`;
    case 'decision_created':
      return m.summary || m.decision_type || 'Neue Entscheidung';
    case 'phase_changed':
      return `Phase ${m.old_phase} → ${m.new_phase}`;
    case 'role_changed':
      return `${m.target_user_name || 'Mitglied'}: ${m.old_role} → ${m.new_role}`;
    case 'member_joined':
      return `Als ${m.role || 'Mitglied'} beigetreten`;
    default:
      return event.activity_type;
  }
}

export function AuditTrailPanel({ workspaceId, embedded = false }: AuditTrailPanelProps) {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [exporting, setExporting] = useState(false);

  const MAX_VISIBLE = 8;

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('activity_logs')
        .select(`
          id, created_at, activity_type, entity_type, metadata,
          profiles ( name, email )
        `)
        .eq('workspace_id', workspaceId)
        .in('activity_type', [
          'classification_overridden',
          'classification_confirmed',
          'decision_created',
          'phase_changed',
          'role_changed',
          'member_joined',
        ])
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      setEvents(
        (data || []).map((d: any) => ({
          id: d.id,
          created_at: d.created_at,
          activity_type: d.activity_type,
          entity_type: d.entity_type,
          metadata: d.metadata,
          user_name: d.profiles?.name || '-',
          user_email: d.profiles?.email || '-',
        }))
      );
    } catch (err) {
      console.error('Failed to load audit trail:', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const summary = await exportClassificationAuditJSON(workspaceId);
      toast.success(
        `Audit exportiert: ${summary.total_classifications} Klassifizierungen, ${summary.user_overrides} Overrides (${summary.override_rate_percent}%)`
      );
    } catch (err: any) {
      toast.error('Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  // Stats
  const overrideCount = events.filter((e) => e.activity_type === 'classification_overridden').length;
  const confirmedCount = events.filter((e) => e.activity_type === 'classification_confirmed').length;
  const totalClassifications = overrideCount + confirmedCount;
  const overrideRate = totalClassifications > 0 ? Math.round((overrideCount / totalClassifications) * 100) : 0;

  const visibleEvents = isExpanded ? events : events.slice(0, MAX_VISIBLE);
  const hasMore = events.length > MAX_VISIBLE;

  if (loading) {
    return (
      <div className={cn('p-6 space-y-3', !embedded && 'rounded-2xl border border-border/50 bg-card')}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="skeleton-shimmer w-8 h-8 rounded-lg" />
            <div className="flex-1 space-y-1.5">
              <div className="skeleton-shimmer h-3 w-2/3 rounded" />
              <div className="skeleton-shimmer h-2.5 w-1/3 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className={cn(
        'relative overflow-hidden transition-all duration-500',
        !embedded && 'rounded-2xl border border-border/50 bg-card p-6'
      )}>
        <div className={cn('relative z-10', embedded && 'p-6')}>
          {!embedded && (
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Audit Trail</h3>
                <p className="text-sm text-muted-foreground">Entscheidungs- und Override-Historie</p>
              </div>
            </div>
          )}
          <p className={cn(
            'text-sm text-muted-foreground text-center py-6',
            !embedded && 'mt-4 border-t border-border/50'
          )}>
            Noch keine Audit-Einträge vorhanden
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'relative overflow-hidden transition-all duration-500',
      !embedded && 'rounded-2xl border border-border/50 bg-card hover:border-primary/20 hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.12)]'
    )}>
      {!embedded && (
        <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      )}

      <div className={cn('relative z-10', !embedded && 'p-6', embedded && 'p-6')}>
        {/* Header */}
        {!embedded && (
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Audit Trail</h3>
                <p className="text-sm text-muted-foreground">Entscheidungs- und Override-Historie</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={loadEvents}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={handleExport}
                    disabled={exporting}
                  >
                    <Download className={cn('h-3.5 w-3.5', exporting && 'animate-pulse')} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Klassifizierungs-Audit als JSON exportieren</TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}

        {/* Summary Stats */}
        {totalClassifications > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="text-center p-2.5 rounded-xl bg-muted/30 border border-border/50">
              <p className="text-lg font-bold text-foreground">{totalClassifications}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Klassifiziert</p>
            </div>
            <div className="text-center p-2.5 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
              <p className="text-lg font-bold text-emerald-600">{confirmedCount}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Bestätigt</p>
            </div>
            <div className="text-center p-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20">
              <p className="text-lg font-bold text-amber-600">{overrideCount}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Override{overrideRate > 0 && ` (${overrideRate}%)`}
              </p>
            </div>
          </div>
        )}

        {/* Embedded export button */}
        {embedded && totalClassifications > 0 && (
          <div className="flex items-center justify-end gap-1.5 mb-3">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={loadEvents}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Aktualisieren
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleExport}
              disabled={exporting}
            >
              <Download className={cn('h-3 w-3 mr-1', exporting && 'animate-pulse')} />
              Export
            </Button>
          </div>
        )}

        {/* Event Timeline */}
        <div className="space-y-1">
          {visibleEvents.map((event, index) => {
            const config = EVENT_CONFIG[event.activity_type] || {
              label: event.activity_type,
              icon: Activity,
              color: 'text-muted-foreground',
              bgColor: 'bg-muted/30 border-border/50',
            };
            const Icon = config.icon;
            const isOverride = event.activity_type === 'classification_overridden';

            return (
              <div
                key={event.id}
                className={cn(
                  'group flex items-start gap-2.5 p-2.5 rounded-xl transition-all duration-200',
                  'hover:bg-muted/30',
                  isOverride && 'bg-amber-500/[0.03] hover:bg-amber-500/[0.06]'
                )}
              >
                {/* Timeline dot + line */}
                <div className="relative flex-shrink-0">
                  <div className={cn(
                    'w-7 h-7 rounded-lg flex items-center justify-center border transition-all',
                    config.bgColor,
                    'group-hover:scale-105'
                  )}>
                    <Icon className={cn('h-3.5 w-3.5', config.color)} />
                  </div>
                  {index < visibleEvents.length - 1 && (
                    <div className="absolute top-7 left-1/2 -translate-x-1/2 w-px h-[calc(100%+0.25rem)] bg-border/40" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge
                      variant="outline"
                      className={cn('text-[9px] px-1.5 py-0 h-4 font-semibold border', config.bgColor, config.color)}
                    >
                      {config.label}
                    </Badge>
                    <span className="text-xs font-medium text-foreground truncate">
                      {event.user_name}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    {getEventDescription(event)}
                  </p>

                  {/* Override detail row */}
                  {isOverride && event.metadata?.override_delta && (
                    <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-600/80">
                      <ArrowRight className="h-2.5 w-2.5" />
                      <span>
                        {event.metadata.override_delta.from_type || event.metadata.system_classified_type}
                        {' → '}
                        {event.metadata.override_delta.to_type || event.metadata.final_type}
                        {event.metadata.override_delta.subtype_changed && (
                          <> · Subtyp angepasst</>
                        )}
                      </span>
                    </div>
                  )}

                  <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                    {formatDistanceToNow(new Date(event.created_at), { locale: de, addSuffix: true })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Show more/less */}
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full mt-3 text-xs text-muted-foreground hover:text-primary hover:bg-primary/5"
          >
            {isExpanded ? (
              <>Weniger anzeigen <ChevronUp className="h-3.5 w-3.5 ml-1" /></>
            ) : (
              <>+{events.length - MAX_VISIBLE} weitere Einträge <ChevronDown className="h-3.5 w-3.5 ml-1" /></>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
